import { action, observable, set } from 'mobx';
import {
    addToMap,
    bind,
    ConnectionManager,
    HttpClient,
    IAccessTokenStore,
    JsonHubProtocol,
    setMap,
} from 'Mri.Web.Common';

export interface IConfig {
    method?: string;
    data?: string | {};
    headers?: Headers;
}

export interface IImportModel {
    files: FileList;
}

export class ManifestsStore {
    public constructor(httpClient: HttpClient, accessTokenStore: IAccessTokenStore, manifestsStore?: ManifestsStore) {
        if (manifestsStore) set(this, manifestsStore);
        this.httpClient = httpClient;
        this.accessTokenStore = accessTokenStore;
        this.connectionManager = new ConnectionManager('/sockets/softwareUpdateManifest', accessTokenStore);
        const { connection } = this.connectionManager;
        connection.on('OnStatusChange', this.onStatusChange);
    }

    @action
    public async loadAllAsync() {
        const displaymanifests = await this.httpClient.get<Mri.SoftwareUpdateManifests.SoftwareUpdateManifest[]>(
            `/api/v1/software-update-manifests`
        );

        setMap(this.manifests, displaymanifests, d => d.softwareUpdateManifestGuid);
    }

    @action
    public async createAsync(model: Mri.SoftwareUpdateManifests.SoftwareUpdateManifest) {
        const manifest = await this.httpClient.post<Mri.SoftwareUpdateManifests.SoftwareUpdateManifest>(
            `/api/v1/software-update-manifests`,
            model
        );

        if (manifest) this.manifests.set(manifest.softwareUpdateManifestGuid, manifest);
    }

    @action
    public async loadAsync(guid: string) {
        const manifest = await this.httpClient.get<Mri.SoftwareUpdateManifests.SoftwareUpdateManifest>(
            `/api/v1/software-update-manifests/${guid}`
        );

        if (manifest) this.manifests.set(manifest.softwareUpdateManifestGuid, manifest);
    }

    @action
    public async updateAsync(guid: string, model: Mri.SoftwareUpdateManifests.SoftwareUpdateManifest) {
        const manifest = await this.httpClient.put<Mri.SoftwareUpdateManifests.SoftwareUpdateManifest>(
            `/api/v1/software-update-manifests/${guid}/update-manifest`,
            model
        );

        if (manifest) this.manifests.set(manifest.softwareUpdateManifestGuid, manifest);
    }

    @action
    public async deleteAsync(guid: string) {
        const manifest = await this.httpClient.delete<Mri.SoftwareUpdateManifests.SoftwareUpdateManifest>(
            `/api/v1/software-update-manifests/${guid}/delete-manifest`
        );

        if (manifest) this.manifests.delete(manifest.softwareUpdateManifestGuid);
    }

    @action
    public async loadStatusAsync(guid: string) {
        const manifestStatus = await this.httpClient.get<Mri.SoftwareUpdateManifests.SoftwareUpdateManifestStatus>(
            `/api/v1/software-update-manifests/${guid}/status`
        );

        if (manifestStatus) this.manifestStatuses.set(manifestStatus.softwareUpdateManifestGuid, manifestStatus);
    }

    @action
    public async clearAsync(guid: string) {
        const manifestStatus = await this.httpClient.put<Mri.SoftwareUpdateManifests.SoftwareUpdateManifestStatus>(
            `/api/v1/software-update-manifests/${guid}/status/clear`
        );

        if (manifestStatus) this.manifestStatuses.set(manifestStatus.softwareUpdateManifestGuid, manifestStatus);
    }

    @action
    public async updateNowAsync(guid: string) {
        await this.httpClient.put(`/api/v1/software-update-manifests/${guid}/update`);
    }

    @action
    public async updateDisplayNowAsync(guid: string, serialNumber: string) {
        await this.httpClient.put(`/api/v1/software-update-manifests/${guid}/${serialNumber}/update`);
    }

    @action
    public async cloneAsync(guid: string, model: Mri.SoftwareUpdateManifests.SoftwareUpdateManifest) {
        const manifest = await this.httpClient.put<Mri.SoftwareUpdateManifests.SoftwareUpdateManifest>(
            `/api/v1/software-update-manifests/${guid}/clone`,
            model
        );

        if (manifest) this.manifests.set(manifest.softwareUpdateManifestGuid, manifest);
    }

    @action
    public async activateAsync(guid: string) {
        const manifest = await this.httpClient.put<Mri.SoftwareUpdateManifests.SoftwareUpdateManifest>(
            `/api/v1/software-update-manifests/${guid}/active`
        );

        if (manifest) this.manifests.set(manifest.softwareUpdateManifestGuid, manifest);
    }

    @action
    public async archiveAsync(guid: string) {
        const manifest = await this.httpClient.put<Mri.SoftwareUpdateManifests.SoftwareUpdateManifest>(
            `/api/v1/software-update-manifests/${guid}/archive`
        );

        if (manifest) this.manifests.delete(manifest.softwareUpdateManifestGuid);
    }

    @action
    public async deactivateAsync(guid: string) {
        const manifest = await this.httpClient.delete<Mri.SoftwareUpdateManifests.SoftwareUpdateManifest>(
            `/api/v1/software-update-manifests/${guid}/active`
        );

        if (manifest) this.manifests.set(manifest.softwareUpdateManifestGuid, manifest);
    }

    @action
    public async subscribeToManifestAsync(guid: string) {
        console.debug('SubscribeToManifest', guid);
        await this.connectionManager.connection.send('SubscribeToManifestAsync', guid);
    }

    @action
    public async unsubscribeToManifestAsync(guid: string) {
        console.debug('UnsubscribeToManifest', guid);
        await this.connectionManager.connection.send('UnsubscribeToManifest', guid);
    }
    
    @action
    public async cancelDisplayUpdateAsync(serialNumber: string) {
        let status = await this.httpClient.put(`cancel-update-for-unit/${serialNumber}`);
        console.log(status);
    }

    @bind
    @action
    private onStatusChange(model: Mri.SoftwareUpdateManifests.SoftwareUpdateManifestStatus) {
        console.debug('onStatusChange', model);

        if (model) this.manifestStatuses.set(model.softwareUpdateManifestGuid, model);
    }

    @observable public manifests = new Map<string, Mri.SoftwareUpdateManifests.SoftwareUpdateManifest>();
    @observable public manifestStatuses = new Map<string, Mri.SoftwareUpdateManifests.SoftwareUpdateManifestStatus>();
    @observable public connected: boolean = false;
    public readonly connectionManager: ConnectionManager;

    private readonly httpClient: HttpClient;
    private readonly accessTokenStore: IAccessTokenStore;
}
